import React from "react";
import { Box, Typography } from "@mui/material";
import { T, cardSx, PIPELINE_COLORS, MOTION } from "utils/backrrTokens";
import { formatCount } from "utils/chartsPalette";
import InfoHint from "./InfoHint";

// The four states an invitation can be in right now, as ONE stacked bar rather
// than a funnel chart: the segments are disjoint and sum to the invitations
// sent, so a bar states that relationship honestly in a tenth of the height a
// funnel's trapezoids would need.
const SEGMENTS = [
  { key: "invited", label: "Invited, No Response" },
  { key: "interested", label: "Interested" },
  { key: "invested", label: "Invested" },
  { key: "rejected", label: "Rejected" },
];

// 0.72rem / 0.875rem, not sizes of their own: the page already carries a step
// for small meta (the tiles' footnotes) and one for card titles, and a scale
// gains nothing from a fourth value half a pixel from the third.
const Stat = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle }}>{label}</Typography>
    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Typography>
  </Box>
);

const PipelineCard = ({ pipeline, matchRate, isLoading }) => {
  if (isLoading) {
    return <Box className="skeleton" sx={{ ...cardSx, height: "16rem" }} />;
  }

  const total = pipeline?.total ?? 0;
  const rows = SEGMENTS.map((s) => ({ ...s, count: pipeline?.[s.key] ?? 0 }));

  // A rate of null means nothing was invited; it must not render as 0%.
  const answered = (pipeline?.interested ?? 0) + (pipeline?.invested ?? 0);
  const toInterested = total ? Math.round(((pipeline?.interested ?? 0) / total) * 1000) / 10 : null;
  const toInvested = answered ? Math.round(((pipeline?.invested ?? 0) / answered) * 1000) / 10 : null;

  return (
    // Even spacing throughout: one fixed gap between legend rows, never a
    // distribute-to-fill. Stretching the rows apart to swallow the row's spare
    // height just trades a visible gap for four invisible ones. The chart card
    // beside this one is sized to match instead, so there is little spare
    // height left to argue about.
    <Box sx={{ ...cardSx, padding: "14px 16px", display: "flex", flexDirection: "column" }}>
      <Box display="flex" alignItems="center">
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Pipeline</Typography>
        <InfoHint
          label="Pipeline"
          text="Every invitation sent in this range, grouped by the status it holds right now. The four states are disjoint and add up to the total, so the bar can be read as a proportion."
        />
      </Box>
      <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle, marginTop: 0.5 }}>
        {total === 0
          ? "No invitations in this range"
          : `${formatCount(total)} invitations sent in this range · where they stand now`}
      </Typography>

      <Box
        role="img"
        aria-label={
          total === 0
            ? "No invitations to show"
            : rows.map((r) => `${r.label}: ${r.count}`).join(", ")
        }
        sx={{
          display: "flex",
          gap: "3px",
          height: 10,
          borderRadius: "5px",
          overflow: "hidden",
          marginTop: 2,
          backgroundColor: total === 0 ? T.grid : "transparent",
        }}
      >
        {total > 0 &&
          rows
            .filter((r) => r.count > 0)
            .map((r) => (
              <Box
                key={r.key}
                sx={{ width: `${(r.count / total) * 100}%`, backgroundColor: PIPELINE_COLORS[r.key], ...MOTION.sx }}
              />
            ))}
      </Box>

      <Box sx={{ marginTop: 2, display: "grid", gap: 1.5 }}>
        {rows.map((r) => (
          <Box key={r.key} display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "3px",
                flexShrink: 0,
                backgroundColor: PIPELINE_COLORS[r.key],
              }}
            />
            <Typography sx={{ flex: 1, fontSize: "0.78rem", color: T.inkMuted }}>{r.label}</Typography>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {r.count}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          marginTop: 2,
          paddingTop: 2,
          borderTop: `1px solid ${T.border}`,
          display: "flex",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Stat label="Invited → Interested" value={toInterested === null ? "—" : `${toInterested}%`} />
        <Stat label="Interested → Invested" value={toInvested === null ? "—" : `${toInvested}%`} />
        <Stat
          label="Match Rate"
          value={matchRate?.value === null || matchRate?.value === undefined ? "—" : `${matchRate.value}%`}
        />
      </Box>
    </Box>
  );
};

export default PipelineCard;
